import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { AltioraTreeLogo } from "@/components/brand/AltioraTreeLogo";
import { api } from "@/lib/api";

const CANVAS = "#FCFBF7";
const HAIRLINE = "#E8E1D0";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const GOLD = "#B98F3D";
const IVORY = "#FBF6EA";
const CHAMPAGNE = "#C5A880";

const PROFESSIONS = [
  "Conseiller financier", "Courtier hypothécaire", "Comptable", "Fiscaliste",
  "Recruteur", "Consultant", "Entrepreneur", "Institution/Coopérative", "Autre",
];
const TEAM_SIZES = ["1 (moi seul)", "2 à 10", "11 à 50", "51 à 200", "200+"];


export default function DemoRequest() {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", role: "",
    profession: "", team_size: "", message: "",
  });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const update = (k, v) => setForm({ ...form, [k]: v });
  const canSubmit = form.name && form.email && form.profession;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      await api.post("/landing/demo", form);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: CANVAS }} data-testid="demo-root">
      <nav className="border-b" style={{ borderColor: HAIRLINE }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <AltioraTreeLogo size={26} />
            <span className="font-serif text-[17px]" style={{ color: INK }}>ALTIORA</span>
          </Link>
          <Link to="/" className="text-[13px] hover:opacity-70" style={{ color: INK_SOFT }} data-testid="demo-back">
            <ArrowLeft className="h-3.5 w-3.5 inline mr-1" /> Retour
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
        {done ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="demo-thanks">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full mb-6"
                 style={{ background: `${GOLD}22`, color: GOLD }}>
              <Check className="h-6 w-6" strokeWidth={2} />
            </div>
            <h1 className="font-serif text-[36px] md:text-[48px] leading-tight tracking-tight" style={{ color: INK }}>
              Merci. <span style={{ color: GOLD, fontStyle: "italic" }}>Nous vous contactons sous 48 h.</span>
            </h1>
            <p className="mt-6 text-[16px] leading-[1.7] max-w-xl" style={{ color: INK_SOFT }}>
              Un membre de l'équipe ALTIORA vous proposera un créneau pour une
              démonstration personnalisée de la plateforme, adaptée à votre métier
              et à votre organisation.
            </p>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-5 gap-12">
            <div className="md:col-span-2">
              <div className="text-[10px] font-medium uppercase tracking-[0.32em]" style={{ color: CHAMPAGNE }}>
                Démonstration privée
              </div>
              <h1 className="mt-6 font-serif text-[36px] md:text-[52px] leading-[1.05] tracking-tight" style={{ color: INK }}>
                Voir ALTIORA <span style={{ color: GOLD, fontStyle: "italic" }}>en contexte réel.</span>
              </h1>
              <p className="mt-6 text-[15px] leading-[1.7]" style={{ color: INK_SOFT }}>
                Une session de 30 minutes avec un membre de l'équipe. Nous adaptons la
                démonstration à votre profession, à vos décisions les plus fréquentes
                et à votre organisation.
              </p>
              <p className="mt-6 text-[13px]" style={{ color: INK_SOFT }}>
                Réponse sous 48 heures ouvrables.
              </p>
            </div>

            <form onSubmit={submit} className="md:col-span-3 space-y-4" data-testid="demo-form">
              <Field label="Prénom et nom" required>
                <input value={form.name} onChange={(e) => update("name", e.target.value)}
                       className="w-full rounded-lg px-4 py-3 text-[15px]"
                       style={{ border: `1px solid ${HAIRLINE}`, background: "#FFF" }}
                       data-testid="demo-name" required />
              </Field>
              <Field label="Courriel professionnel" required>
                <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                       className="w-full rounded-lg px-4 py-3 text-[15px]"
                       style={{ border: `1px solid ${HAIRLINE}`, background: "#FFF" }}
                       data-testid="demo-email" required />
              </Field>
              <Field label="Organisation">
                <input value={form.organization} onChange={(e) => update("organization", e.target.value)}
                       className="w-full rounded-lg px-4 py-3 text-[15px]"
                       style={{ border: `1px solid ${HAIRLINE}`, background: "#FFF" }}
                       data-testid="demo-org" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Votre profession" required>
                  <select value={form.profession} onChange={(e) => update("profession", e.target.value)}
                          className="w-full rounded-lg px-4 py-3 text-[15px] bg-white"
                          style={{ border: `1px solid ${HAIRLINE}` }}
                          data-testid="demo-profession" required>
                    <option value="">Choisir…</option>
                    {PROFESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Taille de l'équipe">
                  <select value={form.team_size} onChange={(e) => update("team_size", e.target.value)}
                          className="w-full rounded-lg px-4 py-3 text-[15px] bg-white"
                          style={{ border: `1px solid ${HAIRLINE}` }}
                          data-testid="demo-team">
                    <option value="">Choisir…</option>
                    {TEAM_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Message (optionnel)">
                <textarea value={form.message} onChange={(e) => update("message", e.target.value)}
                          rows={4}
                          className="w-full rounded-lg px-4 py-3 text-[15px]"
                          style={{ border: `1px solid ${HAIRLINE}`, background: "#FFF" }}
                          data-testid="demo-message"
                          placeholder="Quelle décision aimeriez-vous voir simulée ?" />
              </Field>
              {error && <p className="text-[12px]" style={{ color: "#B91C1C" }}>{error}</p>}
              <button type="submit" disabled={saving || !canSubmit}
                      data-testid="demo-submit"
                      className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-medium disabled:opacity-40"
                      style={{ background: INK, color: IVORY }}>
                {saving ? "Envoi en cours…" : "Demander une démonstration"} <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}


function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-mono uppercase tracking-[0.22em] mb-1.5 block" style={{ color: INK_SOFT }}>
        {label} {required && <span style={{ color: GOLD }}>*</span>}
      </span>
      {children}
    </label>
  );
}
